import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import QuillEditor from '../../../components/MasterAdmin/ui/QuillEditor';
import BlogSeoFields, {
  BlogSeoFormValues,
  emptyBlogSeoValues,
} from '../../../components/Writer/Blogs/BlogSeoFields';
import { getCategories } from '../../../services/Writer/blogCategoriesService';
import { createBlog } from '../../../services/Writer/blogService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import ReferenceLinksField, {
  validateAndNormalizeReferenceLinks,
} from '../../../components/Writer/ReferenceLinksField';
import { Calendar } from 'lucide-react';

type Category = { categoryId: string; name: string };

const toDateInput = (value?: string | null) => {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const BlogFormPage: React.FC = () => {
  const navigate = useNavigate();
  const userId = useMemo(() => localStorage.getItem('userId') || '', []);
  const blogDateInputRef = useRef<HTMLInputElement | null>(null);

  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [blogDate, setBlogDate] = useState(toDateInput(new Date().toISOString()));
  const [seo, setSeo] = useState<BlogSeoFormValues>(emptyBlogSeoValues);

  // Quill
  const [editorValue, setEditorValue] = useState<any>(''); // can be HTML or Delta
  const [contentOpsJson, setContentOpsJson] = useState<string>('');

  const [featuredImageFile, setFeaturedImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [referenceLinks, setReferenceLinks] = useState<string[]>(['']);

  useEffect(() => {
    if (!featuredImageFile) {
      setImagePreview(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(featuredImageFile);
    setImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [featuredImageFile]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingCategories(true);
        const data = await getCategories();
        setCategories(data);
      } catch (e: any) {
        toast.error(getApiErrorMessage(e, 'Failed to load categories'));
      } finally {
        setLoadingCategories(false);
      }
    };
    load();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) {
      toast.error('Missing userId. Please logout and login again.');
      return;
    }
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!categoryId) {
      toast.error('Category is required');
      return;
    }

    const contentToSend = contentOpsJson || String(editorValue || '').trim();
    if (!contentToSend) {
      toast.error('Content is required');
      return;
    }

    let normalizedLinks: string[] | null = null;
    try {
      normalizedLinks = validateAndNormalizeReferenceLinks(referenceLinks);
    } catch (err: any) {
      toast.error(err?.message || 'Invalid reference link');
      return;
    }

    setSubmitting(true);
    try {
      await createBlog({
        title: title.trim(),
        content: contentToSend,
        categoryId,
        status,
        blogDate: blogDate ? new Date(`${blogDate}T00:00:00`).toISOString() : undefined,
        authorId: userId,
        authorType: 'writer',
        featuredImageFile,
        referenceLinks: normalizedLinks,
        metaTitle: seo.metaTitle.trim() || null,
        metaDescription: seo.metaDescription.trim() || null,
        slug: seo.slug.trim() || null,
        focusKeyword: seo.focusKeyword.trim() || null,
        secondaryKeywords: seo.secondaryKeywords.trim() || null,
        recommendedSchema: seo.recommendedSchema.trim() || null,
      });
      toast.success('Blog created');
      setTimeout(() => navigate('/writer/blogs'), 600);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Failed to create blog'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
      <ToastContainer position="top-right" autoClose={2500} />
      <div className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Create Blog</div>

      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title *</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            placeholder="Enter title"
            maxLength={200}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category *</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={loadingCategories}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              <option value="">{loadingCategories ? 'Loading...' : 'Select category'}</option>
              {categories.map((c) => (
                <option key={c.categoryId} value={c.categoryId}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Blog Date</label>
          <div
            className="relative cursor-pointer"
            onClick={() => {
              const el = blogDateInputRef.current;
              if (!el) return;
              try {
                // Chromium: opens picker on any click when allowed.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (el as any).showPicker?.();
              } catch {
                // ignore (some browsers restrict it)
              }
              el.focus();
            }}
          >
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-100 pointer-events-none" />
            <input
              ref={blogDateInputRef}
              type="date"
              value={blogDate}
              onChange={(e) => setBlogDate(e.target.value)}
              onClick={(e) => {
                // Ensure clicking inside input also opens picker in Chromium.
                const el = e.currentTarget;
                try {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (el as any).showPicker?.();
                } catch {
                  // ignore
                }
              }}
              className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            />
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            This is the publish date you can backdate for older blogs.
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Content *</label>
          <QuillEditor
            value={editorValue}
            onChange={(html) => setEditorValue(html)}
            onChangeDelta={(ops) => setContentOpsJson(JSON.stringify(ops))}
            height="360px"
            placeholder="Write your blog..."
          />
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Stored in DB as Quill Delta JSON (same as Flutter doctor blogs).
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Featured Image</label>
          {imagePreview ? (
            <div className="mb-2">
              <img
                src={imagePreview}
                alt="Featured preview"
                className="max-h-56 rounded-lg border border-gray-200 dark:border-gray-700 object-contain"
              />
              {featuredImageFile ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Selected: {featuredImageFile.name}
                </p>
              ) : null}
            </div>
          ) : null}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              if (file && !file.type.startsWith('image/')) {
                toast.error('Please select a valid image file');
                e.target.value = '';
                setFeaturedImageFile(null);
                return;
              }
              setFeaturedImageFile(file);
            }}
            className="block w-full text-sm text-gray-500 dark:text-gray-400"
          />
        </div>

        <ReferenceLinksField value={referenceLinks} onChange={setReferenceLinks} />

        <BlogSeoFields value={seo} onChange={setSeo} />

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/writer/blogs')}
            className="px-5 py-2.5 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 transition"
          >
            {submitting ? 'Saving...' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogFormPage;
